// Fetch wrapper that always sends cookies and handles JSON errors uniformly.
// JWT is in httpOnly cookie — no token management needed client-side.
// On 401, redirect to /login so the router picks it up.

const BASE = '';  // same-origin in prod; Vite proxy handles /api/* in dev

export class ApiError extends Error {
  constructor(
    public readonly statusCode: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...init?.headers },
    ...init,
  });

  if (res.status === 204) return undefined as T;

  const json = await res.json().catch(() => ({ message: res.statusText }));

  if (!res.ok) {
    const message = (json as { message?: string }).message ?? 'Request failed';
    // Let callers decide on 401 handling; throw with status for router-level redirect
    throw new ApiError(res.status, message, json);
  }

  return json as T;
}

export const api = {
  get: <T>(path: string, init?: RequestInit) => request<T>(path, init),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

// -----------------------------------------------------------------------
// Typed API helpers
// -----------------------------------------------------------------------

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// Landlords
export const landlordsApi = {
  list: (params: Record<string, string | number | boolean | undefined>) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') qs.set(k, String(v));
    }
    const query = qs.toString();
    return api.get<PaginatedResponse<import('@imza/shared').LandlordListItem>>(
      `/api/landlords${query ? '?' + query : ''}`
    );
  },
  // Per-unit list — one row per unit, joined to parent landlord.
  // Replaces landlordsApi.list() for the main /landlords page (Phase 2 of Backlog #02).
  listUnits: (params: Record<string, string | number | boolean | undefined>) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') qs.set(k, String(v));
    }
    const query = qs.toString();
    return api.get<PaginatedResponse<import('@imza/shared').UnitListItem>>(
      `/api/units${query ? '?' + query : ''}`
    );
  },
  // Admin-only inline edit of a unit's view / beds / unit_no
  patchUnit: (unitId: number, body: Partial<import('@imza/shared').PatchUnit>) =>
    api.patch<{ id: number; unitNo: string | null; beds: string | null; view: string | null }>(
      `/api/units/${unitId}`, body
    ),
  get: (id: number) =>
    api.get<import('@imza/shared').LandlordDetail & {
      recentActivity: Array<{
        id: number;
        userId: number | null;
        userName: string | null;
        type: string;
        detail: Record<string, unknown> | null;
        createdAt: string;
      }>;
    }>(`/api/landlords/${id}`),
  patch: (id: number, body: Partial<import('@imza/shared').PatchLandlord>) =>
    api.patch<{ id: number; name: string; stage: string; updated_at: string }>(
      `/api/landlords/${id}`, body
    ),
  getActivity: (id: number, page = 1, pageSize = 50) =>
    api.get<PaginatedResponse<import('@imza/shared').ActivityEvent>>(
      `/api/landlords/${id}/activity?page=${page}&pageSize=${pageSize}`
    ),
  getPortfolio: (id: number) =>
    api.get<{
      name: string;
      landlordIds: number[];
      phones: Array<{ id: number; phone: string; isMobile: boolean; isPrimary: boolean; isSwitchboard: boolean }>;
      emails: Array<{ id: number; email: string; isPrimary: boolean }>;
      totalUnits: number;
      totalBuildings: number;
      totalAreas: number;
      areas: Array<{
        areaId: number | null;
        areaName: string | null;
        buildings: Array<{
          landlordId: number;
          building: string | null;
          stage: string;
          // sourcePrimaryPhone: the source profile's primary mobile,
          // surfaced as a chip in the drawer so users can tell apart
          // same-name profiles by phone (Sherzod 2026-05-06 name-group).
          sourcePrimaryPhone: string | null;
          units: Array<{ id: number; unitNo: string | null; beds: string | null; view: string | null; stage: string }>;
        }>;
      }>;
    }>(`/api/landlords/${id}/portfolio`),
  addTag: (id: number, tag_id: number) =>
    api.post(`/api/landlords/${id}/tags`, { tag_id }),
  removeTag: (id: number, tagId: number) =>
    api.delete(`/api/landlords/${id}/tags/${tagId}`),
  // Phones live on the lead. The unit-card phone input is a shortcut
  // that POSTs here with from_unit_no breadcrumb. Delete is gated on
  // role server-side (user/super_admin only).
  addPhone: (id: number, phone: string, is_primary: boolean, from_unit_no?: string | null) =>
    api.post<{ id: number; phone: string; isPrimary: boolean; createdAt: string }>(
      `/api/landlords/${id}/phones`,
      { phone, is_primary, from_unit_no: from_unit_no ?? undefined }
    ),
  removePhone: (id: number, phoneId: number) =>
    api.delete<void>(`/api/landlords/${id}/phones/${phoneId}`),
};

// Activity
export const activityApi = {
  log: (body: import('@imza/shared').CreateActivity) =>
    api.post<import('@imza/shared').ActivityEvent>('/api/activity', body),
  list: (params: Record<string, string | number | undefined>) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) qs.set(k, String(v));
    }
    return api.get<PaginatedResponse<import('@imza/shared').ActivityEvent & {
      landlordName?: string | null;
    }>>(`/api/activity?${qs.toString()}`);
  },
};

// Areas
export const areasApi = {
  list: () => api.get<{ items: Array<{ id: number; name: string }>; total: number }>('/api/areas'),
};

// Buildings
export const buildingsApi = {
  list: (area_id?: number) => {
    const qs = area_id ? `?area_id=${area_id}` : '';
    return api.get<{ items: Array<{ name: string; area_id: number | null; areaName: string | null; unitCount: number }>; total: number }>(
      `/api/buildings${qs}`
    );
  },
  listPaginated: (params: {
    q?: string;
    area_id?: number;
    page?: number;
    pageSize?: number;
    sort?: 'name' | 'units_desc';
  }) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.area_id !== undefined) qs.set('area_id', String(params.area_id));
    if (params.page !== undefined) qs.set('page', String(params.page));
    if (params.pageSize !== undefined) qs.set('pageSize', String(params.pageSize));
    if (params.sort) qs.set('sort', params.sort);
    const query = qs.toString();
    return api.get<PaginatedResponse<import('@imza/shared').BuildingListItem>>(
      `/api/buildings/list${query ? '?' + query : ''}`
    );
  },
};

// Tags
export const tagsApi = {
  list: () => api.get<{ items: Array<{ id: number; name: string }>; total: number }>('/api/tags'),
  create: (name: string) =>
    api.post<{ id: number; name: string }>('/api/tags', { name }),
};

// Agents (lightweight list for manager dropdowns)
export const agentsApi = {
  list: () =>
    api.get<{ items: Array<{ id: number; name: string }>; total: number }>('/api/agents'),
};

// Users
export const usersApi = {
  list: () =>
    api.get<{ items: Array<import('@imza/shared').UserPublic & { generatedPassword?: string }>; total: number }>(
      '/api/users'
    ),
  create: (body: { email: string; name: string; role: string; password?: string }) =>
    api.post<import('@imza/shared').UserPublic & { generatedPassword?: string }>(
      '/api/users', body
    ),
  patch: (id: number, body: { name?: string; role?: string; active?: boolean }) =>
    api.patch<import('@imza/shared').UserPublic>(`/api/users/${id}`, body),
  resetPassword: (id: number) =>
    api.post<{ userId: number; newPassword: string }>(`/api/users/${id}/reset-password`),
  delete: (id: number) =>
    api.delete<void>(`/api/users/${id}`),
};

// Assignments
export const assignmentsApi = {
  list: (params: { agent_id?: number; area_id?: number }) => {
    const qs = new URLSearchParams();
    if (params.agent_id) qs.set('agent_id', String(params.agent_id));
    if (params.area_id) qs.set('area_id', String(params.area_id));
    return api.get<{ items: Array<import('@imza/shared').Assignment>; total: number }>(
      `/api/assignments?${qs.toString()}`
    );
  },
  create: (body: import('@imza/shared').CreateAssignment, force = false) =>
    api.post<import('@imza/shared').Assignment>(
      `/api/assignments${force ? '?force=true' : ''}`, body
    ),
  delete: (id: number) =>
    api.delete<void>(`/api/assignments/${id}`),
  agentAssignments: (agentId: number) =>
    api.get<{ items: Array<{
      assignmentId: number;
      landlordId: number;
      landlordName: string;
      areaId: number | null;
      areaName: string | null;
      building: string | null;
      stage: string;
      assignedAt: string;
    }>; total: number }>(`/api/agents/${agentId}/assignments`),
};

// KPI
export const kpiApi = {
  agent: (agentId: number, period: 'today' | 'week' | 'month' = 'today') =>
    api.get<import('@imza/shared').AgentKpi>(`/api/kpi/agent/${agentId}?period=${period}`),
  team: (period: 'today' | 'week' | 'month' = 'today', managerId?: number) => {
    const qs = managerId ? `?manager_id=${managerId}&period=${period}` : `?period=${period}`;
    return api.get<import('@imza/shared').TeamKpi>(`/api/kpi/team${qs}`);
  },
  system: (period: 'today' | 'week' | 'month' = 'today') =>
    api.get<import('@imza/shared').SystemKpi>(`/api/kpi/system?period=${period}`),
};

// Managers
export const managersApi = {
  getAreas: (managerId: number) =>
    api.get<{ managerId: number; areas: Array<{ id: number; name: string }> }>(
      `/api/managers/${managerId}/areas`
    ),
  setAreas: (managerId: number, area_ids: number[]) =>
    api.post<{ managerId: number; areas: Array<{ id: number; name: string }> }>(
      `/api/managers/${managerId}/areas`, { area_ids }
    ),
};

// Stats (dashboard overview)
export const statsApi = {
  overview: () =>
    api.get<{
      recordsByArea: Array<{ area: string; records: number; pct: number }>;
      topBuildings: Array<{ building: string; units: number }>;
      totalRecords: number;
    }>('/api/stats/overview'),
};

// Units — Slice C of Backlog 08
export const unitsApi = {
  patch: (unitId: number, body: import('@imza/shared').PatchUnitExtended) =>
    api.patch<{
      id: number;
      unitNo: string | null;
      beds: string | null;
      view: string | null;
      stage: string;
      notes: string | null;
      assigneeId: number | null;
      followupDate: string | null;
      updatedAt: string;
    }>(`/api/units/${unitId}`, body),
  getComments: (unitId: number) =>
    api.get<Array<{
      id: number;
      unitId: number;
      authorId: number;
      authorName: string;
      body: string;
      createdAt: string;
    }>>(`/api/units/${unitId}/comments`),
  addComment: (unitId: number, body: string) =>
    api.post<{
      id: number;
      unitId: number;
      authorId: number;
      body: string;
      createdAt: string;
    }>(`/api/units/${unitId}/comments`, { body }),
  addTag: (unitId: number, tag_id: number) =>
    api.post<{ unitId: number; tagId: number }>(`/api/units/${unitId}/tags`, { tag_id }),
  removeTag: (unitId: number, tagId: number) =>
    api.delete<void>(`/api/units/${unitId}/tags/${tagId}`),
  getPhones: (unitId: number) =>
    api.get<Array<{
      id: number;
      unitId: number;
      phone: string;
      isPrimary: boolean;
      createdAt: string;
    }>>(`/api/units/${unitId}/phones`),
  addPhone: (unitId: number, phone: string, is_primary: boolean) =>
    api.post<{
      id: number;
      unitId: number;
      phone: string;
      isPrimary: boolean;
      createdAt: string;
    }>(`/api/units/${unitId}/phones`, { phone, is_primary }),
  removePhone: (unitId: number, phoneId: number) =>
    api.delete<void>(`/api/units/${unitId}/phones/${phoneId}`),
};

// Reminders (bell-icon backend — Slice D of Backlog 08)
export const remindersApi = {
  list: (status: 'unread' | 'done' | 'all' = 'unread', limit = 50) =>
    api.get<{ items: Array<import('@imza/shared').Reminder> }>(
      `/api/reminders?status=${status}&limit=${limit}`
    ),
  count: () => api.get<{ unread: number }>('/api/reminders/count'),
  patch: (id: number, body: import('@imza/shared').PatchReminder) =>
    api.patch<import('@imza/shared').Reminder>(`/api/reminders/${id}`, body),
  createForUnit: (unitId: number, body: { body?: string | null; due_at: string }) =>
    api.post<import('@imza/shared').Reminder>(`/api/units/${unitId}/reminders`, body),
};

// -----------------------------------------------------------------------
// Admin — junk review queue
// -----------------------------------------------------------------------

export interface JunkReviewItem {
  id: number;
  name: string;
  building: string | null;
  topUnitNo: string | null;
  phoneCount: number;
  hasEmail: boolean;
  archivedAt: string | null;
  archivedByName: string | null;
  archiveReason: string | null;
  daysArchived: number;
  eligibleForHardDelete: boolean;
}

export interface JunkReviewParams {
  status?: 'archived' | 'active' | 'all';
  flag?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}

export const adminApi = {
  junkReview: (params: JunkReviewParams) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.flag) qs.set('flag', params.flag);
    if (params.q) qs.set('q', params.q);
    if (params.page !== undefined) qs.set('page', String(params.page));
    if (params.pageSize !== undefined) qs.set('pageSize', String(params.pageSize));
    const query = qs.toString();
    return api.get<PaginatedResponse<JunkReviewItem>>(
      `/api/landlords/junk-review${query ? '?' + query : ''}`
    );
  },
  archive: (id: number, reason: string) =>
    api.post<{ archivedAt: string }>(`/api/landlords/${id}/archive`, { reason }),
  restore: (id: number) =>
    api.post<{ archivedAt: null }>(`/api/landlords/${id}/restore`),
  hardDelete: (id: number, force = false) =>
    api.delete<void>(`/api/landlords/${id}/hard${force ? '?force=true' : ''}`),
};

// Dashboard (Backlog 01 + Phase 2)
export const dashboardApi = {
  admin: (period: import('@imza/shared').KpiPeriod = 'week') =>
    api.get<import('@imza/shared').AdminDashboard>(`/api/dashboard/admin?period=${period}`),
  manager: (period: import('@imza/shared').KpiPeriod = 'week') =>
    api.get<import('@imza/shared').ManagerDashboard>(`/api/dashboard/manager?period=${period}`),
};

// Imports (Backlog 06 — Phase 1: preview, Phase 2: commit)
export const importsApi = {
  preview: async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${BASE}/api/imports/preview`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    const json = await res.json().catch(() => ({ message: res.statusText }));
    if (!res.ok) {
      throw new ApiError(res.status, (json as { message?: string }).message ?? 'Upload failed', json);
    }
    return json as import('@imza/shared').ImportPreviewResponse;
  },

  commit: (fileToken: string, mapping: import('@imza/shared').ColumnMapping) =>
    api.post<import('@imza/shared').ImportCommitResponse>('/api/imports/commit', {
      fileToken,
      mapping,
    }),
};

// Export (returns a download URL — navigate to it)
export const exportCsvUrl = (params: Record<string, string | undefined>): string => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') qs.set(k, v);
  }
  return `/api/export/landlords.csv?${qs.toString()}`;
};
